import { useState } from 'react';
import { Frame } from '@/marketing/Frame';
import { AnimatePresence, motion } from 'framer-motion';
import { Reveal, useReducedMotion } from '@/lib/motion';
import { cn } from '@/lib/utils';

/**
 * Per-stage accent colours mirror the /investor pages' signal semantics:
 * quant rank = blue, technical confirmation = green, risk overlay = gold,
 * qualitative downside/trim = red (SignalForge only ever cuts risk).
 */
type Accent = { base: string; soft: string; glow: string };

const STAGES: {
  key: string;
  index: string;
  name: string;
  role: string;
  flow: string;
  question: string;
  body: string;
  accent: Accent;
}[] = [
  {
    key: 'fin',
    index: '01',
    name: 'FinIntellect',
    role: 'Quantitative rank',
    flow: '500 → ~100',
    question:
      'Which stocks are most likely to outperform, based on everything history tells us?',
    body: 'A machine-learning model scores every name in the S&P 500 on momentum, valuation and earnings quality — validated across non-overlapping history to resist overfitting. The output is a ranked shortlist, not a bet.',
    accent: { base: '#38bdf8', soft: 'rgba(56,189,248,0.12)', glow: 'rgba(56,189,248,0.30)' },
  },
  {
    key: 'tech',
    index: '02',
    name: 'TechniQ',
    role: 'Technical confirmation',
    flow: '~100 → ~60',
    question: 'Of those, which are technically confirmed and safe to trade right now?',
    body: 'Filters the shortlist on volume, risk-adjusted momentum and trend position. A great fundamental score still has to be the right trade today — names in broken structure are dropped.',
    accent: { base: '#3dff85', soft: 'rgba(61,255,133,0.12)', glow: 'rgba(61,255,133,0.26)' },
  },
  {
    key: 'risk',
    index: '03',
    name: 'RiskGuard',
    role: 'Market-risk overlay',
    flow: 'risk-sized',
    question: 'Given today’s market, how much should we hold — and how much in cash?',
    body: 'Predicts near-term downside and sets total equity exposure, from fully invested to a defensive cash posture. It sizes the book to the regime; it doesn’t pick the names.',
    accent: { base: '#f7941d', soft: 'rgba(247,148,29,0.14)', glow: 'rgba(247,148,29,0.30)' },
  },
  {
    key: 'sig',
    index: '04',
    name: 'SignalForge',
    role: 'Qualitative due diligence',
    flow: '→ 20–30',
    question: 'Is there anything in the filings or insider activity that says reduce?',
    body: 'Reads SEC filings, insider trades and supply-chain risk, and trims on high-conviction downside the numbers alone can’t see. It only ever cuts risk — the upside is the quant engine’s job.',
    accent: { base: '#ff4d5e', soft: 'rgba(255,77,94,0.12)', glow: 'rgba(255,77,94,0.26)' },
  },
];

const OUT_ACCENT = '#f7941d';

export function Machine() {
  const [active, setActive] = useState(0);
  const reduced = useReducedMotion();
  const stage = STAGES[active];
  const a = stage.accent;

  return (
    <Frame id="engine" label="The engine">
      <div className="max-w-3xl">
        <h2 className="font-display text-[clamp(2rem,4.5vw,3.25rem)] font-bold leading-[1.02] tracking-[-0.02em] text-foreground">
          We read the whole market. Every day.{' '}
          <span className="text-[color:var(--nq-gold)]">Then narrow it to a few.</span>
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
          No black box. Four independent disciplines take the entire S&amp;P 500 and
          narrow it, name by name, to the book the ETF holds.{' '}
          <span className="text-foreground/80">Hover a stage to look inside it.</span>
        </p>
      </div>

      {/* Interactive funnel */}
      <div className="mt-14 overflow-x-auto pb-2">
        <div className="flex min-w-[720px] items-stretch gap-0">
          <Endpoint badge="S&P 500" sub="500 names" tone="in" />
          <Connector active={!reduced} color={STAGES[0].accent.base} />
          {STAGES.map((s, i) => (
            <div key={s.key} className="flex items-stretch">
              <FunnelNode
                stage={s}
                selected={i === active}
                onSelect={() => setActive(i)}
              />
              {i < STAGES.length - 1 && (
                <Connector active={!reduced} color={STAGES[i + 1].accent.base} />
              )}
            </div>
          ))}
          <Connector active={!reduced} color={OUT_ACCENT} />
          <Endpoint badge="20–30" sub="held in the ETF" tone="out" />
        </div>
      </div>

      {/* Detail panel for the selected stage */}
      <div className="relative mt-6 min-h-[176px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={stage.key}
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mkt-glass rounded-2xl p-6 sm:p-8"
            style={{ boxShadow: `inset 3px 0 0 ${a.base}` }}
          >
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="mkt-mono text-xs font-semibold" style={{ color: a.base }}>
                {stage.index}
              </span>
              <h3 className="font-display text-2xl font-semibold text-foreground">
                {stage.name}
              </h3>
              <span className="mkt-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {stage.role}
              </span>
              <span
                className="mkt-mono ml-auto rounded-full border px-2.5 py-1 text-[10px] tracking-[0.12em]"
                style={{ borderColor: a.base, backgroundColor: a.soft, color: a.base }}
              >
                {stage.flow}
              </span>
            </div>
            <p className="mt-4 max-w-2xl font-display text-xl italic leading-snug text-[color:var(--nq-gold-soft)]">
              “{stage.question}”
            </p>
            <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
              {stage.body}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      <Reveal delay={0.05}>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground">
            Every run is auditable end to end:
          </span>
          {['Versioned', 'Hashed', 'Reconstructable'].map((chip) => (
            <span
              key={chip}
              className="mkt-mono rounded-full border border-[color:rgba(37,180,224,0.4)] bg-[color:rgba(37,180,224,0.12)] px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-[color:var(--nq-blue-bright)]"
            >
              {chip}
            </span>
          ))}
        </div>
      </Reveal>
    </Frame>
  );
}

function FunnelNode({
  stage,
  selected,
  onSelect,
}: {
  stage: (typeof STAGES)[number];
  selected: boolean;
  onSelect: () => void;
}) {
  const a = stage.accent;
  return (
    <button
      type="button"
      onMouseEnter={onSelect}
      onFocus={onSelect}
      onClick={onSelect}
      aria-pressed={selected}
      style={
        selected
          ? { borderColor: a.base, backgroundColor: a.soft, boxShadow: `0 0 28px ${a.glow}` }
          : undefined
      }
      className={cn(
        'group relative flex w-40 flex-col justify-between rounded-xl border p-4 text-left transition-all duration-300',
        selected ? 'border' : 'border-white/10 bg-white/[0.02] hover:border-white/25'
      )}
    >
      <div className="flex items-center justify-between">
        <span
          className="mkt-mono text-[11px] font-semibold"
          style={{ color: selected ? a.base : 'rgba(255,255,255,0.35)' }}
        >
          {stage.index}
        </span>
        {/* Each stage always shows its own colour so the four hues read at a glance. */}
        <span
          className="size-2 rounded-full transition-all"
          style={{
            backgroundColor: a.base,
            opacity: selected ? 1 : 0.65,
            boxShadow: selected ? `0 0 10px ${a.base}` : 'none',
          }}
        />
      </div>
      <div className="mt-6">
        <div className="font-display text-base font-semibold text-foreground">
          {stage.name}
        </div>
        <div
          className="mkt-mono mt-1 text-[9px] uppercase tracking-[0.14em]"
          style={{ color: selected ? a.base : 'rgba(148,163,184,0.85)' }}
        >
          {stage.role}
        </div>
      </div>
    </button>
  );
}

function Endpoint({
  badge,
  sub,
  tone,
}: {
  badge: string;
  sub: string;
  tone: 'in' | 'out';
}) {
  return (
    <div
      className={cn(
        'flex w-28 shrink-0 flex-col justify-center',
        tone === 'out' ? 'pl-5' : 'pr-2'
      )}
    >
      <span
        className={cn(
          'font-display text-lg font-bold',
          tone === 'out' ? 'text-[color:var(--nq-gold)]' : 'text-foreground'
        )}
      >
        {badge}
      </span>
      <span className="mkt-mono mt-1 text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
        {sub}
      </span>
    </div>
  );
}

function Connector({ active, color }: { active: boolean; color: string }) {
  return (
    <div className="flex w-10 shrink-0 items-center justify-center self-center">
      <svg viewBox="0 0 40 8" className="h-2 w-full" aria-hidden>
        <line x1="0" y1="4" x2="34" y2="4" stroke="rgba(255,255,255,0.14)" strokeWidth="1.5" />
        {active && (
          <line x1="0" y1="4" x2="34" y2="4" className="mkt-flow" stroke={color} strokeWidth="1.5" />
        )}
        <path d="M34 1 L40 4 L34 7 Z" fill={color} opacity="0.75" />
      </svg>
    </div>
  );
}
